import { t } from "i18next";
import type { FieldErrors, UseFormWatch } from "react-hook-form";
import type { ObjectSchema } from "yup";

import type { FormInputOption, ValidationComparisonData } from "~/types/formTypes.ts";

// removes all non-alphanumeric characters and converts to lowercase and remove all spaces
export const sanitizeStringForComparison = (string: string): string => {
    return string.toLowerCase().replace(/[^a-z0-9]/g, "");
};

export const sanitizeEmailForComparison = (email: string): string => {
    return email.toLowerCase().replace(/[^a-z0-9@.]/g, "");
};

export const sanitiseTitleForComparison = (title: string): string => {
    return title.toLowerCase().replace(/\s+/g, " ");
};

export const mapDataToInputOptionsData = <T>(
    data: T[],
    valuePropName: keyof T,
    labelPropName: keyof T,
    includeDefaultOption?: boolean,
): FormInputOption[] => {
    let selectOptions = data
        .filter((item) => typeof item[valuePropName] === "string" && typeof item[labelPropName] === "string")
        .map((item) => createInputOptionData(item[labelPropName] as string, item[valuePropName] as string));

    if (includeDefaultOption) {
        selectOptions = [getInputDefaultOptionsData(), ...selectOptions];
    }

    return selectOptions;
};

export const createInputOptionData = (displayName: string, value: string): FormInputOption => {
    return {
        label: displayName,
        value,
    };
};

export const createValidationComparisonData = ({
    value,
    id,
}: {
    value: string;
    id: string;
}): ValidationComparisonData => {
    return {
        id,
        value,
    };
};

export const getInputDefaultOptionsData = (labelText?: string, value?: string): FormInputOption => {
    return createInputOptionData(labelText || t("form:select.defaultOption"), value || "");
};

export const preventSubmitOnEnter = (event: React.KeyboardEvent<HTMLFormElement>) => {
    const target = event.target as HTMLElement;
    if (event.key === "Enter" && target.tagName !== "TEXTAREA") {
        event.preventDefault();
    }
};

export function transformEmptyDateValueToNull(value: Date | string, originalValue: Date | string | null) {
    return originalValue === "" ? null : value;
}

export function transformEmptyDateValueToZero(value: number | null, originalValue: number | null) {
    return !originalValue ? 0 : value;
}

export function checkIfAnyFieldIsDirty(dirtyFields: any) {
    // Go through each key in the object
    for (const key in dirtyFields) {
        // If the value is an object, call the function recursively
        if (typeof dirtyFields[key] === "object" && dirtyFields[key] !== null) {
            if (checkIfAnyFieldIsDirty(dirtyFields[key])) {
                return true;
            }
            // If the value is a boolean and it's true, return true
        } else if (dirtyFields[key] === true) {
            return true;
        }
    }
    // If none of the fields are dirty, return false
    return false;
}

function isValidFormDataValue(value: any): value is string | Blob | File {
    return typeof value === "string" || value instanceof Blob || value instanceof File;
}

export const trimFormValues = <T extends Record<string, unknown>>(formValues: T): T => {
    return Object.fromEntries(
        Object.entries(formValues).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value]),
    ) as T;
};

export function createFormDataWithFiles(
    data: Record<string, any>,
    files: Record<string, File | FileList | File[] | null | undefined>,
): FormData {
    const formData = new FormData();

    // Helper function to append nested objects
    const appendNestedObject = (obj: any, prefix = "") => {
        if (obj === undefined) {
            return;
        }

        if (obj === null) {
            formData.append(prefix, "");
            return;
        }

        if (typeof obj === "object" && !(obj instanceof File) && !(obj instanceof FileList)) {
            Object.entries(obj).forEach(([key, value]) => {
                const newPrefix = prefix ? `${prefix}[${key}]` : key;
                appendNestedObject(value, newPrefix);
            });
        } else if (isValidFormDataValue(obj)) {
            formData.append(prefix, obj);
        } else {
            formData.append(prefix, String(obj));
        }
    };

    // Append regular form data
    Object.entries(data).forEach(([key, value]) => {
        if (value === undefined) {
            return;
        }

        if (typeof value === "object" && value !== null && !(value instanceof File) && !(value instanceof FileList)) {
            appendNestedObject(value, key);
        } else {
            formData.append(key, value === null ? "" : String(value));
        }
    });

    // Append files
    Object.entries(files).forEach(([key, value]) => {
        if (value === undefined) {
            return;
        }

        if (value === null) {
            formData.append(key, "");
        } else if (value instanceof File) {
            formData.append(key, value);
        } else if (value instanceof FileList || Array.isArray(value)) {
            Array.from(value).forEach((file) => {
                formData.append(`${key}[]`, file === null || file === undefined ? "" : file);
            });
        } else if (typeof value === "object") {
            // Handle nested files
            Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                if (nestedValue === undefined) {
                    return;
                }

                if (nestedValue === null) {
                    formData.append(`${key}[${nestedKey}]`, "");
                } else if (nestedValue instanceof File) {
                    formData.append(`${key}[${nestedKey}]`, nestedValue);
                } else {
                    // Handle unexpected values by converting to string
                    formData.append(`${key}[${nestedKey}]`, String(nestedValue));
                }
            });
        }
    });

    return formData;
}

export function formValidationDebug(
    formValues: UseFormWatch<any>,
    validators: ObjectSchema<any>,
    formErrors: FieldErrors<any>,
) {
    console.log(validators);
    const validatorKeys = Object.keys(validators.fields);
    const formValueKeys = Object.keys(formValues);
    const missingKeysInValidator = validatorKeys.filter((key) => !formValueKeys.includes(key));
    const missingKeysInFormValue = formValueKeys.filter((key) => !validatorKeys.includes(key));
    console.log({ missingKeysInValidator, missingKeysInFormValue, formErrors });
    console.log(formValues);
}
